import { useCallback, useEffect, useMemo, useState } from "react";
import CamisetasModal from "./CamisetasModal";
import CamisetasPagination from "./CamisetasPagination";
import useCamisetasData from "../hooks/useCamisetasData";

const PAGE_SIZE = 8;
const preloadedImagePromises = new Map();

function preloadImage(url) {
    if (!url || typeof window === "undefined") {
        return Promise.resolve();
    }

    if (preloadedImagePromises.has(url)) {
        return preloadedImagePromises.get(url);
    }

    const preloadPromise = new Promise((resolve) => {
        const image = new Image();
        image.onload = () => resolve();
        image.onerror = () => resolve();
        image.src = url;
    });

    preloadedImagePromises.set(url, preloadPromise);

    return preloadPromise;
}

function preloadImages(urls = []) {
    const uniqueUrls = [...new Set(urls.filter(Boolean))];
    return Promise.all(uniqueUrls.map((url) => preloadImage(url)));
}

function CamisetasGrid({ mode = "rojiverde" }) {
    const rojiverdeCamisetas = useCamisetasData("rojiverde");
    const alternativaCamisetas = useCamisetasData("alternativa");
    const camisetas = useCamisetasData(mode);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [isModalTransitioning, setIsModalTransitioning] = useState(false);

    const totalPages = Math.max(1, Math.ceil(camisetas.length / PAGE_SIZE));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const selectedCamiseta = selectedIndex !== null ? camisetas[selectedIndex] : null;
    const hasPrev = selectedIndex !== null && selectedIndex > 0;
    const hasNext = selectedIndex !== null && selectedIndex < camisetas.length - 1;

    const camisetasByTitle = useMemo(() => {
        const buildMap = (items) =>
            items.reduce((acc, item) => {
                if (!acc[item.title]) {
                    acc[item.title] = [];
                }

                acc[item.title].push(item);
                return acc;
            }, {});

        return {
            rojiverde: buildMap(rojiverdeCamisetas),
            alternativa: buildMap(alternativaCamisetas),
        };
    }, [rojiverdeCamisetas, alternativaCamisetas]);

    const buildPairFromCamiseta = useCallback((camiseta) => {
        if (!camiseta) {
            return null;
        }

        const selectedEdition = Number(camiseta.id.split("-").pop()) - 1;
        const safeEdition = Number.isNaN(selectedEdition) ? 0 : selectedEdition;

        const rojiverdeItems = camisetasByTitle.rojiverde[camiseta.title] ?? [];
        const alternativaItems = camisetasByTitle.alternativa[camiseta.title] ?? [];

        const selectedVariantItems =
            camiseta.variant === "alternativa" ? alternativaItems : rojiverdeItems;

        const getCounterpartItem = (sourceItems, targetItems) => {
            const indexOffset = sourceItems.length - targetItems.length;
            const targetIndex = safeEdition - indexOffset;

            if (targetIndex < 0 || targetIndex >= targetItems.length) {
                return null;
            }

            return targetItems[targetIndex] ?? null;
        };

        const rojiverdeItem =
            camiseta.variant === "rojiverde"
                ? rojiverdeItems[safeEdition] ?? null
                : getCounterpartItem(selectedVariantItems, rojiverdeItems);

        const alternativaItem =
            camiseta.variant === "alternativa"
                ? alternativaItems[safeEdition] ?? null
                : getCounterpartItem(selectedVariantItems, alternativaItems);

        return {
            title: camiseta.title,
            rojiverde: rojiverdeItem,
            alternativa: alternativaItem,
            initialVariantId: camiseta.variant,
        };
    }, [camisetasByTitle]);

    const selectedCamisetasPair = useMemo(
        () => buildPairFromCamiseta(selectedCamiseta),
        [selectedCamiseta, buildPairFromCamiseta]
    );

    const paginatedCamisetas = useMemo(() => {
        const start = (safeCurrentPage - 1) * PAGE_SIZE;
        return camisetas.slice(start, start + PAGE_SIZE);
    }, [camisetas, safeCurrentPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [mode]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const preloadModalNeighborhood = useCallback((centerIndex) => {
        if (centerIndex === null || centerIndex === undefined) {
            return Promise.resolve();
        }

        const candidateIndexes = [centerIndex - 1, centerIndex, centerIndex + 1].filter(
            (index) => index >= 0 && index < camisetas.length
        );

        const urlsToPreload = candidateIndexes.flatMap((index) => {
            const item = camisetas[index];
            const pair = buildPairFromCamiseta(item);

            return [item?.image, pair?.rojiverde?.image, pair?.alternativa?.image].filter(Boolean);
        });

        return preloadImages(urlsToPreload);
    }, [camisetas, buildPairFromCamiseta]);

    useEffect(() => {
        void preloadImages(paginatedCamisetas.map((item) => item.image));
    }, [paginatedCamisetas]);

    useEffect(() => {
        if (selectedIndex === null) {
            return;
        }

        void preloadModalNeighborhood(selectedIndex);
    }, [selectedIndex, preloadModalNeighborhood]);

    const openModalAtIndex = useCallback((index) => {
        if (index < 0 || index >= camisetas.length || isModalTransitioning) {
            return;
        }

        setIsModalTransitioning(true);

        preloadModalNeighborhood(index)
            .then(() => {
                setSelectedIndex(index);
            })
            .finally(() => {
                setIsModalTransitioning(false);
            });
    }, [camisetas.length, isModalTransitioning, preloadModalNeighborhood]);

    const handlePrev = () => {
        if (selectedIndex === null || isModalTransitioning) {
            return;
        }

        const nextIndex = Math.max(0, selectedIndex - 1);
        openModalAtIndex(nextIndex);
    };

    const handleNext = () => {
        if (selectedIndex === null || isModalTransitioning) {
            return;
        }

        const nextIndex = Math.min(camisetas.length - 1, selectedIndex + 1);
        openModalAtIndex(nextIndex);
    };

    if (!camisetas.length) {
        return (
            <section className="rounded-xl border border-white/10 bg-[#0f1f15] p-6 text-center">
                <p className="text-slate-300 font-semibold">No hay camisetas para esta categoría.</p>
            </section>
        );
    }

    return (
        <div>
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
                {paginatedCamisetas.map((item, index) => (
                    <article
                        key={item.id}
                        className="group h-full rounded-xl border border-green-500/30 bg-gradient-to-b from-[#123822] to-[#0b2618] p-2 sm:p-3 shadow-[0_0_24px_rgba(34,197,94,0.14)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_32px_rgba(34,197,94,0.24)]"
                    >
                        <button
                            type="button"
                            onClick={() => openModalAtIndex((safeCurrentPage - 1) * PAGE_SIZE + index)}
                            className="block w-full text-left"
                            aria-label={`Abrir camiseta ${item.title}`}
                        >
                            <div className="aspect-[4/5] overflow-hidden rounded-md border border-green-500/25 bg-[#0a1f14]">
                                <img
                                    src={item.image}
                                    alt={item.title}
                                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                                    loading="lazy"
                                />
                            </div>
                            <div className="pt-3 text-center">
                                <h3 className="text-white text-sm sm:text-base leading-tight font-black uppercase tracking-wide min-h-[2.5rem] flex items-center justify-center">
                                    {item.title}
                                </h3>
                            </div>
                        </button>
                    </article>
                ))}
            </section>

            <CamisetasPagination
                currentPage={safeCurrentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
            />

            <CamisetasModal
                camisetasPair={selectedCamisetasPair}
                isOpen={Boolean(selectedCamiseta)}
                onClose={() => setSelectedIndex(null)}
                onPrev={handlePrev}
                onNext={handleNext}
                hasPrev={hasPrev && !isModalTransitioning}
                hasNext={hasNext && !isModalTransitioning}
                isTransitioning={isModalTransitioning}
            />
        </div>
    );
}

export default CamisetasGrid;
